import { useEffect, useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../providers/auth/useAuth'
import {
  AdManagementService,
  type AspectRatio,
  type BrandProduct,
  type CompetitorAd,
  type GeneratedAdResponse,
} from '../../client'
import { apiErrorMessage } from '../../lib/api'
import DialogMessage from '../ui/DialogMessage'
import Modal from '../ui/Modal'
import MediaPreview from '../home/MediaPreview'
import AdFieldsLoader from '../ads/AdFieldsLoader'
import AdPromptInput, { type Resolution } from '../ads/AdPromptInput'
import ProductPicker from './ProductPicker'
import ProductSlot from './ProductSlot'

type Phase = 'scraping_products' | 'config' | 'generating' | 'error' | 'credits_exhausted'

const MAX_PRODUCTS = 4
const PRODUCT_PROMPT = 'Can you clone this Ad for the given product.'
const COMPANY_PROMPT = 'Can you clone this Ad for my brand.'

const ROUND = 'absolute top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white transition hover:bg-black/60'

export type CopyStarted = {
  ads: GeneratedAdResponse[]
  adIndices: number[]
  referenceUrl: string
  creditsRemaining: number
  creditsLimit: number
}

type Props = {
  brandId: number
  ad: CompetitorAd
  cardIndex: number
  onClose: () => void
  onGenerated: (payload: CopyStarted) => void
}

export default function CloneAdDialog({ brandId, ad, cardIndex, onClose, onGenerated }: Props) {
  const { brand } = useAuth()
  const cards = ad.cards ?? []
  const [index, setIndex] = useState(cardIndex)
  const [phase, setPhase] = useState<Phase>('config')
  const [products, setProducts] = useState<BrandProduct[]>([])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [companySelected, setCompanySelected] = useState(false)
  const [showPicker, setShowPicker] = useState(false)
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [showCustom, setShowCustom] = useState(false)
  const [prompt, setPrompt] = useState('')
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1')
  const [resolution, setResolution] = useState<Resolution>('1k')
  const [credits, setCredits] = useState({ consumed: 0, limit: 0, remaining: 0 })
  const [preview, setPreview] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const reference = cards[index]?.image_url || cards[index]?.video_preview_image_url || ''
  const brandName = brand?.name ?? ''
  const website = brand?.website ?? ''
  const host = website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
  const favicon = host ? `https://www.google.com/s2/favicons?domain=${host}&sz=128` : null
  const selectedProducts = products.filter(({ id }) => selectedIds.includes(id))

  useEffect(() => {
    async function prepare() {
      try {
        const usage = await AdManagementService.credits(brandId)
        setCredits(usage)
        if (usage.remaining === 0 && usage.limit > 0) {
          setPhase('credits_exhausted')
          return
        }

        const listed = await AdManagementService.listProducts(brandId)
        if (listed.products_extracted) {
          setProducts(listed.products ?? [])
          return
        }

        setPhase('scraping_products')
        const extracted = await AdManagementService.extractProducts(brandId)
        setProducts(extracted.products ?? [])
        setPhase('config')
      } catch (err) {
        setMessage(apiErrorMessage(err))
        setPhase('error')
      } finally {
        setLoadingProducts(false)
      }
    }

    void prepare()
  }, [brandId])

  function toggleProduct(product: BrandProduct) {
    if (companySelected) return

    setSelectedIds((current) =>
      current.includes(product.id)
        ? current.filter((id) => id !== product.id)
        : current.length < MAX_PRODUCTS
          ? [...current, product.id]
          : current,
    )
  }

  function toggleCompany() {
    if (selectedIds.length > 0) return
    setCompanySelected((current) => !current)
  }

  async function submit() {
    if (submitting) return

    setSubmitting(true)
    setPhase('generating')
    setMessage('')

    try {
      const response = await AdManagementService.copyAd(brandId, {
        reference_image_url: reference,
        product_image_urls: selectedProducts
          .map(({ image_urls }) => image_urls?.[0])
          .filter((url): url is string => Boolean(url)),
        prompt: prompt.trim() || (companySelected ? COMPANY_PROMPT : PRODUCT_PROMPT),
        num_versions: 1,
        aspect_ratio: aspectRatio,
        resolution,
      })

      if (response.credits_exhausted) {
        setPhase('credits_exhausted')
        return
      }
      if (response.success === false) {
        setMessage(response.error_message ?? 'Clone Ad failed. Please try again.')
        setPhase('error')
        return
      }

      const startIndex = response.ad_start_index ?? 0
      onGenerated({
        ads: response.ads ?? [],
        adIndices: (response.ads ?? []).map((_, position) => startIndex + position),
        referenceUrl: response.reference_competitor_ad_url ?? reference,
        creditsRemaining: Math.max(0, credits.remaining - 1),
        creditsLimit: credits.limit,
      })
    } catch (err) {
      setMessage(apiErrorMessage(err))
      setPhase('error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Modal
        title="Clone Ad"
        width="max-w-3xl"
        maxHeight="max-h-[96vh]"
        padded={false}
        onClose={onClose}
        headerRight={
          credits.limit > 0 && (
            <span className="text-xs text-gray-400">
              {credits.consumed} / {credits.limit} credits used
            </span>
          )
        }
        footer={
          phase === 'config' && (
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm ring-1 ring-slate-300 transition ring-inset hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting || !reference}
                onClick={() => void submit()}
                className="rounded bg-purple-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-purple-950 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? 'Starting…' : 'Clone Ad'}
              </button>
            </div>
          )
        }
      >
        {phase === 'scraping_products' || phase === 'generating' ? (
          <div className="relative h-[520px] overflow-hidden">
            <AdFieldsLoader
              variant={phase === 'scraping_products' ? 'scrape-products' : 'generate-ads'}
              isActive
            />
          </div>
        ) : phase === 'error' || phase === 'credits_exhausted' ? (
          <DialogMessage variant={phase === 'error' ? 'error' : 'credits'} message={message} onClose={onClose} />
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto px-7 py-5 [scrollbar-width:thin]">
            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-700">Reference Ad</h3>
              <div className="grid grid-cols-1 items-center gap-4 sm:grid-cols-[1fr_auto_0.7fr]">
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <div className="relative bg-slate-100">
                    {reference && <img src={reference} alt={ad.page_name ?? ''} className="block h-auto w-full" />}
                    {cards.length > 1 && (
                      <>
                        <button
                          type="button"
                          aria-label="Previous card"
                          onClick={() => setIndex((current) => (current - 1 + cards.length) % cards.length)}
                          className={`${ROUND} left-2`}
                        >
                          <ChevronLeftIcon className="size-4" />
                        </button>
                        <button
                          type="button"
                          aria-label="Next card"
                          onClick={() => setIndex((current) => (current + 1) % cards.length)}
                          className={`${ROUND} right-2`}
                        >
                          <ChevronRightIcon className="size-4" />
                        </button>
                        <span className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-2 py-0.5 text-[10px] text-white">
                          Card {index + 1} / {cards.length}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-center text-xl font-light text-slate-300">+</div>

                <div
                  className={`overflow-hidden rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 ${
                    showPicker ? '' : 'flex aspect-square items-center justify-center'
                  }`}
                >
                  {showPicker ? (
                    <ProductPicker
                      brandId={brandId}
                      products={products}
                      selectedIds={selectedIds}
                      companySelected={companySelected}
                      brandName={brandName}
                      brandFavicon={favicon}
                      maxSelected={MAX_PRODUCTS}
                      loading={loadingProducts}
                      onToggleProduct={toggleProduct}
                      onToggleCompany={toggleCompany}
                      onProductsChange={(updated) => {
                        setProducts(updated)
                        setSelectedIds((current) =>
                          current.filter((id) => updated.some((product) => product.id === id)),
                        )
                      }}
                      onPreview={setPreview}
                      onClose={() => setShowPicker(false)}
                    />
                  ) : (
                    <ProductSlot
                      companySelected={companySelected}
                      products={selectedProducts}
                      brandName={brandName}
                      favicon={favicon}
                      host={host}
                      onOpenPicker={() => setShowPicker(true)}
                    />
                  )}
                </div>
              </div>
            </div>

            {!showCustom ? (
              <button
                type="button"
                onClick={() => setShowCustom(true)}
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 transition hover:text-indigo-700"
              >
                <PlusIcon className="size-4" />
                Customize Ad
              </button>
            ) : (
              <div>
                <AdPromptInput
                  prompt={prompt}
                  onPromptChange={setPrompt}
                  aspectRatio={aspectRatio}
                  onAspectRatioChange={setAspectRatio}
                  resolution={resolution}
                  onResolutionChange={setResolution}
                  placeholder="e.g., 'Keep the same layout but feature my product, modern minimalist style'"
                />
                <button
                  type="button"
                  onClick={() => setShowCustom(false)}
                  className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 transition hover:text-slate-700"
                >
                  <ChevronUpIcon className="size-3.5" />
                  Hide customization
                </button>
              </div>
            )}
          </div>
          )}

      </Modal>

      <MediaPreview src={preview} onClose={() => setPreview(null)} />
    </>
  )
}
